import { Component, inject, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { ActivatedRoute, Router } from '@angular/router';
import { FirebaseFirestoreService } from '../../../services/firebase.firestore.service';
import { Batch, CartItem, OptimizedRoute, OrderDetails, OrderStatus, UserPersonalization } from '../../../constants';
import { TimelineModule } from 'primeng/timeline';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { DialogModule } from 'primeng/dialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { FirebaseFunctionsService } from '../../../services/firebase.functions.service';
import { DocumentReference } from '@angular/fire/firestore';
import { ImageModule } from 'primeng/image';
import { PanelModule } from 'primeng/panel';
import { MessageService } from 'primeng/api';
import { ConfirmationService } from 'primeng/api';
import { BatchShoppingCompleteTasksComponent } from '../batch-shopping-complete-tasks/batch-shopping-complete-tasks.component';
import { PreDeliveryCompleteTasksComponent } from '../pre-delivery-complete-tasks/pre-delivery-complete-tasks.component';
import { AddShopperNotesModalComponentComponent } from '../add-shopper-notes-modal-component/add-shopper-notes-modal-component.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UtilityService } from '../../../services/utility.service';

@Component({
  selector: 'app-batch-details',
  standalone: true,
  imports: [
    CommonModule, 
    ButtonModule,
    TimelineModule,
    DialogModule,
    ConfirmDialogModule,
    ImageModule,
    PanelModule
  ],
  providers: [
    DialogService,
    MessageService,
    ConfirmationService
  ],
  templateUrl: './batch-details.component.html',
  styleUrls: ['./batch-details.component.scss'],
  host: {
    class: 'app-batch-details'
  },
  encapsulation: ViewEncapsulation.None
})
export class BatchDetailsComponent {
  batch: Batch | null = null;
  orders: OrderDetails[] = [];
  ordersMetrics: Record<string, {
    allItemsMarked: boolean;
    hasReceipt: boolean;
  }> = {};
  activeStoreItemsByOrder: Record<string, (CartItem & {orderId: string, orderRef: DocumentReference, orderNumber: string, customerDetails: {name: string, phoneNumber: string}, customerPreferences: UserPersonalization})[]> = {};
  activeStore: string | null = null;
  activeRoute: OptimizedRoute | null = null;
  dynamicDialogRef = inject(DynamicDialogRef, { optional: true });
  dynamicDialogConfig = inject(DynamicDialogConfig, { optional: true });
  route: ActivatedRoute = inject(ActivatedRoute);
  router: Router = inject(Router);
  firebaseFirestoreService: FirebaseFirestoreService = inject(FirebaseFirestoreService);
  firebaseFunctionsService: FirebaseFunctionsService = inject(FirebaseFunctionsService);
  utilityService: UtilityService = inject(UtilityService);
  messageService = inject(MessageService);
  dialogService = inject(DialogService);
  confirmationService = inject(ConfirmationService);
  isDialog = false;
  acceptBatchInProgress = false;
  startShoppingInProgress = false;
  continueShoppingInProgress = false;
  completeDeliveryInProgress = false;
  navigateToDropoffInProgress = false;

  constructor() {
    this.loadBatchData();
    
  }

  loadBatchData(): void {
    let batchId: string | null = null;
    
    // Check if we're in a dialog context
    if (this.dynamicDialogConfig?.data?.batchId) {
      this.isDialog = true;
      batchId = this.dynamicDialogConfig.data.batchId;
    }
    
    // If not in dialog or no batchId in dialog config, get from route
    if (!batchId) {
      batchId = this.route.snapshot.paramMap.get('id');
    }
    
    if (!batchId) {
      console.error('No batch ID provided');
      return;
    }

    // Load batch details from Firestore
    this.firebaseFirestoreService.getBatchById(batchId).pipe(takeUntilDestroyed()).subscribe({
      next: async (batchData) => {
        this.batch = batchData;
        // Check if every route has either shopping or delivery completed
        const allRoutesCompleted = this.batch.optimizedRoute?.every((route: OptimizedRoute) => 
          route.shoppingStatus === 'completed' || route.deliveryStatus === 'completed'
        );
        
        if (allRoutesCompleted && this.batch.status !== 'completed') {
          // update batch status to completed
          await this.firebaseFirestoreService.updateBatch(this.batch?.batchId as string, {status: 'completed', completedAt: new Date().valueOf(), completedAtString: new Date().toLocaleString()});
          this.batch.status = 'completed';
        }
        console.log('batchData', batchData);
      },
      error: (error) => {
        console.error('Error loading batch:', error);
      }
    });
  }

  async acceptBatch() {
    this.acceptBatchInProgress = true;  
    if (this.batch) {
      const userProfile = this.firebaseFirestoreService.getUserProfile();
      await this.firebaseFunctionsService.acceptBatchOrder(this.batch.batchId, userProfile.uid, userProfile.name, userProfile.phoneNumber, this.utilityService.location || {latitude: 0, longitude: 0});    
    }
    this.acceptBatchInProgress = false;
  }

  async completeStoreShopping(storeName: string) {
    this.batch?.optimizedRoute?.forEach((route: OptimizedRoute) => {
      if (route.name === storeName) {
        route.shoppingStatus = 'completed';
      }
    });

    this.firebaseFirestoreService.updateBatch(this.batch?.batchId as string, {optimizedRoute: this.batch?.optimizedRoute as OptimizedRoute[] });
  }

  async startShopping(route: OptimizedRoute) {
    this.startShoppingInProgress = true;
    const orders = await this.getActiveStoreItemsByOrder(route);

    // update batch status to in_progress
    // update route status to shopping inprogress
    this.batch?.optimizedRoute?.forEach((route: OptimizedRoute) => {
      if (route.name === this.activeStore) {
        route.shoppingStatus = 'in_progress';
      }
    });

    this.firebaseFirestoreService.updateBatch(this.batch?.batchId as string, {status: 'in_progress',  optimizedRoute: this.batch?.optimizedRoute as OptimizedRoute[] });

    // update route orders status to shopping inprogress
    orders.forEach(order => {
      this.firebaseFirestoreService.updateOrderDetails(order.orderDocumentRef as DocumentReference, {orderStatus: OrderStatus.ShoppingInProgress});
    });
    this.startShoppingInProgress = false;
  }

  async continueShopping(route: OptimizedRoute) {
    this.continueShoppingInProgress = true;
    // set active shopping store name, get orders from route.orderRefs, set shopping status to in_progress, update order status to shopping inprogress, group items by order and active store, update batch status to in_progress
    await this.getActiveStoreItemsByOrder(route);
    this.continueShoppingInProgress = false;
  }

  async getActiveStoreItemsByOrder(route: OptimizedRoute) {
    this.activeRoute = route; 
    this.activeStore = route.name;
    console.log('route', route);
    this.orders = await Promise.all(route.orderRefs.map(ref => this.firebaseFirestoreService.getOrderDetails(ref) as Promise<OrderDetails>));
    console.log('orders', this.orders);
    // group items by order and active store
    const activeStoreItemsFromAllOrders = this.orders.flatMap(order => 
      order.items.map(item => ({
        ...item, 
        orderId: order.orderId, 
        orderRef: order.orderDocumentRef,
        orderNumber: order.orderNumber,
        customerDetails: order.customerDetails,
        customerPreferences: order.customerPreferences
      }))
    ).filter(item => item.store === this.activeStore) as (CartItem & {orderId: string, orderRef: DocumentReference, orderNumber: string, customerDetails: {name: string, phoneNumber: string}, customerPreferences: UserPersonalization})[];
    // groupby items by order and active store, groupby items by order and active store
    this.activeStoreItemsByOrder = activeStoreItemsFromAllOrders.reduce((acc, item) => {
      acc[item.orderId] = acc[item.orderId] || [];
      acc[item.orderId].push(item);
      return acc as Record<string, (CartItem & {orderId: string, orderRef: DocumentReference, orderNumber: string, customerDetails: {name: string, phoneNumber: string}, customerPreferences: UserPersonalization})[]>;
    }, {} as Record<string, (CartItem & {orderId: string, orderRef: DocumentReference, orderNumber: string, customerDetails: {name: string, phoneNumber: string}, customerPreferences: UserPersonalization})[]>);

    // mark if all items are found in each order for that store and also check if order receipts has receipt for the store
    this.orders.forEach(order => {
      const allItemsMarked = !!order.stores.find(store => store.name === this.activeStore)?.allItemsMarked;
      const hasReceipt = order.storeReceipts?.some(receipt => receipt.storeName === this.activeStore) || !!order.stores.find(store => store.name === this.activeStore)?.hasReceipt;
      this.ordersMetrics[order.orderId as string] = {
        allItemsMarked: allItemsMarked,
        hasReceipt: hasReceipt || false
      };
    });
    this.ordersMetrics = {...this.ordersMetrics};
    console.log('ordersMetrics', this.ordersMetrics);

    console.log('activeStoreItemsByOrder', this.activeStoreItemsByOrder);
    return this.orders;
  }

  markItemAsShopped(item: CartItem & {orderId: string, orderRef: DocumentReference, orderNumber: string}) {
    item.status = 'found';
    const orderItems = this.orders.find(order => order.orderId === item.orderId)?.items;
    // update order items with found item
    orderItems?.forEach(orderItem => {
      if (orderItem.id === item.id) {
        orderItem.status = 'found';
      }
    });
    // update order details
    this.firebaseFirestoreService.updateOrderDetails(item.orderRef as DocumentReference, {items: orderItems});
  }

  markItemAsReplaced(item: CartItem & {orderId: string, orderRef: DocumentReference, orderNumber: string}) {
    item.status = 'replaced';
    const orderItems = this.orders.find(order => order.orderId === item.orderId)?.items;
    // update order items status to replaced
    orderItems?.forEach(orderItem => {
      if (orderItem.id === item.id) {
        orderItem.status = 'replaced';
      }
    });
    // update order details
    this.firebaseFirestoreService.updateOrderDetails(item.orderRef as DocumentReference, {items: orderItems});
  }

  markItemAsNotFound(item: CartItem & {orderId: string, orderRef: DocumentReference, orderNumber: string}) {
    item.status = 'not_found';
    const orderItems = this.orders.find(order => order.orderId === item.orderId)?.items;
    // update order items status to not_found
    orderItems?.forEach(orderItem => {
      if (orderItem.id === item.id) {orderItem.status = 'not_found';}
    });
    // update order details
    this.firebaseFirestoreService.updateOrderDetails(item.orderRef as DocumentReference, {items: orderItems});
  }

  addShopperNotes(item: CartItem & {orderId: string, orderRef: DocumentReference, orderNumber: string}) {
    const dialogRef = this.dialogService.open(AddShopperNotesModalComponentComponent, {  
      modal: true,
      closable: true,
      closeOnEscape: true,
      showHeader: true,
      header: 'Add Shopper Notes',
      contentStyle: {
        'height': 'auto',
        'overflow': 'auto'
      },
      position: 'bottom',
      style: {
        'width': '100%',
        'max-width': '640px',
        'height': 'auto',
        'border-radius': '0',
        'border': 'none',
        'margin': '0'
      },
      data: {item}
    });
    dialogRef.onClose.subscribe(async(notes: string) => {
      if(notes) {
        item.shopperNotes = notes;
      }
    });
  }

  completeDeliveryTasks(route: OptimizedRoute) {
    if(!this.batch?.optimizedRoute?.every(r => !r.shoppingStatus || r.shoppingStatus === 'completed')) {
      this.confirmationService.confirm({
        message: 'Please complete shopping from all stores before beginning delivery',
        header: 'Complete Shopping',
        acceptLabel: 'Got It!',
        acceptVisible: false,
        rejectVisible: false,
        position: 'bottom',
        accept: () => {
          return;
        },
        reject: () => {
          return;
        }
      });
      return;
    }

    const dialogRef = this.dialogService.open(PreDeliveryCompleteTasksComponent, {
      modal: true,
      closable: true,
      closeOnEscape: false,
      showHeader: true,
      header: 'Upload Proof Of Delivery',
      contentStyle: {
        'height': 'auto',
        'overflow': 'auto'
      },
      position: 'bottom',
      style: {
        'width': '100%',
        'max-width': '640px',
        'height': 'auto',
        'max-height': '80vh',
        'border-radius': '0',
        'border': 'none',
        'margin': '0'
      },
      data: this.orders.find(order => order.orderId === route.orderIds[0])
    });
    dialogRef.onClose.subscribe(async(goodToMoveToNextStatus) => {
      if(goodToMoveToNextStatus) {
        await this.completeDelivery(route);
      }
    });
  }

  async completeDelivery(route: OptimizedRoute) {
    this.completeDeliveryInProgress = true;
    // set delivery status to in_progress
    this.batch?.optimizedRoute?.forEach((r: OptimizedRoute) => {
      if (r.fullAddress === route.fullAddress) {
        r.deliveryStatus = 'completed';
      }
    });
    this.firebaseFirestoreService.updateBatch(this.batch?.batchId as string, {optimizedRoute: this.batch?.optimizedRoute as OptimizedRoute[] });
    // update order details with delivery status to completed
    await this.firebaseFirestoreService.updateOrderDetails(route.orderRefs[0] as DocumentReference, {
      orderStatus: OrderStatus.Delivered, 
      deliveryDateTime: new Date().valueOf(),
      deliveryDateTimeString: new Date().toLocaleString(),
      completedById: this.firebaseFirestoreService.getUserProfile().uid,
    });

    // update batch details with delivery status to completed
    this.completeDeliveryInProgress = false;
  }

  async navigateToDropoff(route: OptimizedRoute) {
    this.navigateToDropoffInProgress = true;
    // set delivery status to completed
    this.batch?.optimizedRoute?.forEach((r: OptimizedRoute) => {
      if (r.name === route.name) {
        r.deliveryStatus = 'on_the_way';
      }
    });
    this.firebaseFirestoreService.updateBatch(this.batch?.batchId as string, {optimizedRoute: this.batch?.optimizedRoute as OptimizedRoute[] });
    // update order details with delivery status to on_the_way
    this.firebaseFirestoreService.updateOrderDetails(route.orderRefs[0] as DocumentReference, {orderStatus: OrderStatus.OnTheWay});
    this.navigateToDropoffInProgress = false;
  }

  async navigateToStore(route: OptimizedRoute) {
    console.log('navigateToStore', route);
  }

  goBackToBatchDetails() {
    this.activeStore = null;
  }

  closeDialog() {
    if (this.isDialog && this.dynamicDialogRef) {
      this.dynamicDialogRef.close();
    } else {
      this.router.navigate(['/home']);
    }
  }

  confirmItemsChecked(orderId: string, storeName: string) {
    const order = this.orders.find(order => order.orderId === orderId) as OrderDetails;
    const orderItems = order.items.filter(item => item.store === storeName);
    const allItemsMarked = orderItems.every(item => item.status === 'found' || item.status === 'replaced' || item.status === 'not_found');

    if(!allItemsMarked) {
      this.confirmationService.confirm({
        message: 'Please mark every item status as either found, replaced or not found',
        header: 'Mark Item Status',
        acceptVisible: false,
        rejectVisible: false,
        position: 'bottom',
      });
      return false;
    }; 
    return true;  
  }

  markShoppingCompleted(orderId: string, storeName: string) {
    if(!this.confirmItemsChecked(orderId, storeName)) {
      return;
    }
    const order = this.orders.find(order => order.orderId === orderId) as OrderDetails;
    order.stores.forEach(store => {
      if(store.name === storeName) {
        store.allItemsMarked = true;
      }
    });
    this.ordersMetrics[order.orderId as string].allItemsMarked = true;
    this.ordersMetrics = {...this.ordersMetrics};
    this.firebaseFirestoreService.updateOrderDetails(order.orderDocumentRef as DocumentReference, {stores: order.stores});
  }

  showPreShoppingCompleteTasksModal(orderId: string, storeName: string) {
    if(!this.ordersMetrics[orderId as string].allItemsMarked) {
      this.confirmationService.confirm({
        message: 'Please click on mark as done button if you marked all items as either found, replaced or not found',
        header: 'Is Shopping Done?',
        acceptVisible: false,
        rejectVisible: false,
        position: 'bottom',
        closeOnEscape: true,
        closable: true
      });
      return;
    }
    const order = this.orders.find(order => order.orderId === orderId) as OrderDetails;
    const dialogRef = this.dialogService.open(BatchShoppingCompleteTasksComponent, {
      modal: true,
      closable: true,
      closeOnEscape: false,
      showHeader: true,
      header: 'Upload Store Receipts',
      contentStyle: {
        'height': 'auto',
        'overflow': 'auto'
      },
      position: 'bottom',
      style: {
        'width': '100%',
        'max-width': '640px',
        'height': 'auto',
        'max-height': '80vh',
        'border-radius': '0',
        'border': 'none',
        'margin': '0'
      },
      data: {order, storeName}
    });
    dialogRef.onClose.subscribe(async(goodToMoveToNextStatus) => {
      if(goodToMoveToNextStatus) {
        this.ordersMetrics[order.orderId as string].hasReceipt = true;
        this.ordersMetrics = {...this.ordersMetrics};
        order.stores.forEach(store => {
          if(store.name === storeName) {
            store.hasReceipt = true;
            store.allItemsMarked = true;
            store.shoppingCompleted = true;
          }
        });
        await this.firebaseFirestoreService.updateOrderDetails(order.orderDocumentRef as DocumentReference, {stores: order.stores});
        // check if all orders in ordermetrics have hasReceipt true
        const isShoppingForAllOrdersAtThisStoreDone = Object.values(this.ordersMetrics).every(orderMetric => orderMetric.hasReceipt && orderMetric.allItemsMarked);
        if(isShoppingForAllOrdersAtThisStoreDone) {
          // complete shopping for the active store
          await this.completeStoreShopping(storeName);
        }
      }
    });
  }

}